// ══════════════════════════════════════════════════════════════════════════════
// NexGen Auth System v4.0 — Multi-Tenant
// ══════════════════════════════════════════════════════════════════════════════
//
// PLATFORM ROLES (no tenant):
//   hassaan  — owner/super-admin. Sees all tenants, all users, all data.
//   admin    — platform admin. Same as hassaan minus a few protected ops.
//
// TENANT ROLES (scoped to one company):
//   tenant_admin — manages own company's users + seats
//   analyst      — all analysis pages, no user management
//   viewer       — Dashboard, Alerts, Reports only
//   guest        — limited: Dashboard, Alerts, Reports, Threat Intel
//
// Session token carries: username | role | tenant_id | expires_at | sig
// ══════════════════════════════════════════════════════════════════════════════

export const API_BASE_URL = process.env.REACT_APP_API_URL || (
  window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : "https://nexgen-backend-60om.onrender.com"
);

export const PLATFORM_ROLES  = new Set(["hassaan", "admin"]);
export const TENANT_ROLES    = new Set(["tenant_admin", "analyst", "viewer", "guest"]);

// ── Page access matrix ────────────────────────────────────────────────────────
export const PAGE_ACCESS = {
  "/":          ["hassaan", "admin", "tenant_admin", "analyst", "guest", "viewer"],
  "/analytics": ["hassaan", "admin", "tenant_admin", "analyst", "guest"],
  "/alerts":    ["hassaan", "admin", "tenant_admin", "analyst", "guest", "viewer"],
  "/reports":   ["hassaan", "admin", "tenant_admin", "analyst", "guest", "viewer"],
  "/intel":     ["hassaan", "admin", "tenant_admin", "analyst", "guest"],
  "/compliance": ["hassaan", "admin", "tenant_admin", "analyst", "guest"],
  "/live":      ["hassaan", "admin", "tenant_admin", "analyst"],
  "/users":     ["hassaan", "admin", "tenant_admin"],   // tenant_admin sees own users only
  "/tenants":   ["hassaan", "admin"],                   // platform admin only
};

// ── Nav items (sidebar) ───────────────────────────────────────────────────────
export const NAV_ITEMS = [
  { path: "/",          label: "Dashboard",        icon: "⬡", desc: "Anomaly Detection"   },
  { path: "/analytics", label: "Attack Analytics", icon: "◈", desc: "Attack Intelligence" },
  { path: "/alerts",    label: "Cyber Alerts",     icon: "◉", desc: "Live Alert Feed"     },
  { path: "/reports",   label: "Reports",          icon: "◧", desc: "Incident Reports"    },
  { path: "/intel",     label: "Threat Intel",     icon: "◬", desc: "IP · Port · MITRE"  },
  { path: "/compliance", label: "Compliance Gaps",  icon: "⊡", desc: "NIST · IEC · ISO"  },
  { path: "/live",      label: "Live IDS Feed",    icon: "◎", desc: "Ethernet · Real-time"},
  { path: "/users",     label: "User Management",  icon: "⬡", desc: "Access Control"      },
  { path: "/tenants",   label: "Tenant Management",icon: "◫", desc: "Companies · Licenses"},
];

export const ROLE_COLORS = {
  hassaan:      "#FF0033",
  admin:        "#FF3D57",
  tenant_admin: "#FF8C00",
  analyst:      "#00D4FF",
  guest:        "#FFB800",
  viewer:       "#A78BFA",
};

// ── Session helpers ───────────────────────────────────────────────────────────

export function getSession() {
  try {
    return JSON.parse(sessionStorage.getItem("nexgen_user") || "{}");
  } catch {
    return {};
  }
}

export function isPlatformAdmin(role) {
  return PLATFORM_ROLES.has(role);
}

export function isTenantAdmin(role) {
  return role === "tenant_admin";
}

export function isOwner(role) {
  return isPlatformAdmin(role);
}

export function canAccess(role, path) {
  return (PAGE_ACCESS[path] || []).includes(role);
}

// ── Login ─────────────────────────────────────────────────────────────────────

export async function authenticate(username, password, tenant_id = null) {
  try {
    const res = await fetch(`${API_BASE_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, tenant_id }),
    });

    if (res.status === 403) {
      const err = await res.json().catch(() => ({}));
      if (err.detail?.toLowerCase().includes("expired")) return { expired: true };
      return null;
    }
    if (!res.ok) return null;
    return res.json();
  } catch (e) {
    console.error("Login failed", e);
    return null;
  }
}

// ── Authed API helper ─────────────────────────────────────────────────────────

export async function apiRequest(path, options = {}) {
  const session = getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(session.sessionToken ? { Authorization: `Bearer ${session.sessionToken}` } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || `API error ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// ── Tenant API ────────────────────────────────────────────────────────────────

export const TenantsAPI = {
  list:           ()         => apiRequest("/tenants"),
  get:            (id)       => apiRequest(`/tenants/${id}`),
  create:         (body)     => apiRequest("/tenants",     { method: "POST", body: JSON.stringify(body) }),
  update:         (id, body) => apiRequest(`/tenants/${id}`, { method: "PUT",  body: JSON.stringify(body) }),
  delete:         (id)       => apiRequest(`/tenants/${id}`, { method: "DELETE" }),
  regenerateKey:  (id)       => apiRequest(`/tenants/${id}/regenerate-key`, { method: "POST" }),
  seedDemo:       ()         => apiRequest("/tenants/seed-demo", { method: "POST" }),
};

// ── Tenant User API ───────────────────────────────────────────────────────────

export const UsersAPI = {
  list:          (tid)           => apiRequest(`/tenants/${tid}/users`),
  create:        (tid, body)     => apiRequest(`/tenants/${tid}/users`,     { method: "POST",   body: JSON.stringify(body) }),
  update:        (tid, uid, body)=> apiRequest(`/tenants/${tid}/users/${uid}`, { method: "PUT", body: JSON.stringify(body) }),
  delete:        (tid, uid)      => apiRequest(`/tenants/${tid}/users/${uid}`, { method: "DELETE" }),
  resetPassword: (tid, uid, pw)  => apiRequest(`/tenants/${tid}/users/${uid}/reset-password`, { method: "POST", body: JSON.stringify({ new_password: pw }) }),
};

// ── Legacy exports (kept so existing pages don't break) ───────────────────────

export async function getAllUsers() {
  const session = getSession();
  const tid = session.tenant_id;
  if (!tid) return [];
  const data = await UsersAPI.list(tid);
  return data.users || [];
}

export async function createGuestAccount(account) {
  const session = getSession();
  return UsersAPI.create(session.tenant_id, account);
}

export async function deleteGuestAccount(username) {
  const session = getSession();
  const users = await UsersAPI.list(session.tenant_id);
  const user = (users.users || []).find(u => u.username === username);
  if (user) await UsersAPI.delete(session.tenant_id, user.id);
}

export async function saveGuestAccounts(accounts) {
  return accounts;
}

export async function getGuestAccounts() {
  return getAllUsers();
}
